import { create } from 'zustand';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { tokenStore } from '@/api/token-store';
import { useOnboardingStore } from '@/shared/hooks/use-onboarding';
import { useExperienceLevel } from '@/shared/hooks/use-experience-level';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000';
/** Refresh 60s before JWT expiry (ARCHITECTURE.md §13.2) */
const PROACTIVE_REFRESH_BUFFER_MS = 60 * 1000;

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleProactiveRefresh(exp: number) {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);
  const delay = exp * 1000 - PROACTIVE_REFRESH_BUFFER_MS - Date.now();
  if (delay <= 0) return; // Token expiring too soon; reactive 401 refresh handles it
  proactiveRefreshTimer = setTimeout(() => {
    const refreshToken = tokenStore.getRefreshToken();
    if (!refreshToken) return;
    void axios
      .post<{ access_token: string; refresh_token: string }>(
        `${API_BASE}/api/v1/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      )
      .then(({ data }) => {
        // Re-entry: sets new tokens and reschedules proactive refresh
        useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
      })
      .catch(() => {
        // Proactive refresh failed — reactive 401 refresh on next request will handle it
      });
  }, delay);
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'VIEWER' | 'OPERATOR' | 'ADMIN';
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Reason for the last forced logout (null if voluntary) */
  logoutReason: 'security' | null;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  logout: (reason?: 'security') => void;
}

/** Decode JWT payload (base64) — no verification, just reading claims */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Read first truthy string from a list of claim keys */
function claim(payload: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'string' && v.trim()) return v;
    if (typeof v === 'number') return String(v);
  }
  return '';
}

const VALID_ROLES = new Set<AuthUser['role']>(['ADMIN', 'OPERATOR', 'VIEWER']);

function parseUser(accessToken: string): AuthUser | null {
  const payload = decodeJwtPayload(accessToken);
  if (!payload) return null;

  const id = claim(payload, 'sub', 'user_id', 'id');
  const email = claim(payload, 'email', 'user_email');
  const name = claim(payload, 'name', 'full_name', 'display_name', 'preferred_username') || email;
  const rawRole = claim(payload, 'role', 'user_role').toUpperCase() as AuthUser['role'];
  const role = VALID_ROLES.has(rawRole) ? rawRole : 'VIEWER';

  if (import.meta.env.DEV) {
    if (!id) console.warn('[auth] JWT missing user ID claim (sub/user_id/id)');
    if (!email) console.warn('[auth] JWT missing email claim (email/user_email)');
    if (!claim(payload, 'role', 'user_role'))
      console.warn('[auth] JWT missing role claim (role/user_role) — defaulting to VIEWER');
  }

  return { id, email, name, role };
}

/** Try to restore session from existing refresh token on app start */
function getInitialState(): Pick<AuthState, 'user' | 'isAuthenticated' | 'logoutReason'> {
  // Access token is NOT persisted (security). We'll trigger refresh on first 401.
  return { user: null, isAuthenticated: false, logoutReason: null };
}

/**
 * Fetch the current user profile from GET /api/v1/auth/me and update the store.
 * The backend JWT only encodes user_id (sub) — name, email, and role must be fetched.
 * Uses a direct axios call with the stored access token to avoid circular deps with apiClient.
 */
export async function fetchCurrentUser(): Promise<void> {
  const accessToken = tokenStore.getAccessToken();
  if (!accessToken) return;
  try {
    const { data } = await axios.get<{
      id: number;
      email: string;
      name: string;
      role: string;
    }>(`${API_BASE}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const rawRole = (data.role ?? '').toUpperCase() as AuthUser['role'];
    const user: AuthUser = {
      id: String(data.id),
      email: data.email ?? '',
      name: data.name || data.email || '',
      role: VALID_ROLES.has(rawRole) ? rawRole : 'VIEWER',
    };
    useAuthStore.setState({ user });
  } catch {
    // /auth/me failed — keep the partial user from JWT (if any); don't log out
  }
}

/**
 * Attempt silent session restoration from the stored refresh token.
 * Call once at app startup — before rendering protected routes.
 * Resolves regardless of success or failure (never throws).
 */
export async function restoreSession(): Promise<void> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return;
  try {
    const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
      `${API_BASE}/api/v1/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
    await fetchCurrentUser();
  } catch {
    // Refresh token expired or invalid — clear stored token so AuthGuard redirects cleanly
    tokenStore.clear();
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  ...getInitialState(),

  setTokens: (accessToken: string, refreshToken?: string) => {
    tokenStore.setAccessToken(accessToken);
    // Single-use refresh token rotation: backend ALWAYS returns a new refresh_token
    // and the old one is immediately revoked (family revocation on reuse).
    // Always store the new token; callers must provide it.
    if (refreshToken) {
      tokenStore.setRefreshToken(refreshToken);
    }
    const user = parseUser(accessToken);
    set({ user, isAuthenticated: true });
    // Tag Sentry events with the authenticated user (LOK-167)
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email, username: user.name });
    }
    // Schedule proactive refresh 60s before token expiry
    const payload = decodeJwtPayload(accessToken);
    if (payload && typeof payload['exp'] === 'number') {
      scheduleProactiveRefresh(payload['exp']);
    }
  },

  logout: (reason?: 'security') => {
    // Cancel any pending proactive refresh
    if (proactiveRefreshTimer) {
      clearTimeout(proactiveRefreshTimer);
      proactiveRefreshTimer = null;
    }
    // Fire-and-forget server-side logout to invalidate the refresh token family
    const accessToken = tokenStore.getAccessToken();
    if (accessToken) {
      void axios
        .post(`${API_BASE}/api/v1/auth/logout`, null, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        .catch(() => {
          // Best-effort — don't block client-side logout
        });
    }
    // Clear Sentry user context (LOK-167)
    Sentry.setUser(null);
    tokenStore.clear();
    // Reset per-user UI state so next login starts fresh
    useOnboardingStore.getState().reset();
    useExperienceLevel.setState({ level: 'beginner', hasBeenSet: false });
    set({ user: null, isAuthenticated: false, logoutReason: reason ?? null });
    // Broadcast logout to all open tabs (BroadcastChannel + localStorage fallback)
    // Delay import-free: broadcastLogout is defined below in this module
    setTimeout(() => broadcastLogout(), 0);
  },
}));

// Wire up auth-expired callback so API 401s trigger logout via React (no page reload)
tokenStore.onAuthExpired((reason) => {
  useAuthStore.getState().logout(reason);
});

// ── Cross-tab logout synchronization ─────────────────────────────────────────
// Prefer BroadcastChannel (reliable, no storage event noise) with localStorage fallback.
const authChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('trade-sentinel-auth') : null;

if (authChannel) {
  authChannel.onmessage = (event: MessageEvent<{ type: string }>) => {
    if (event.data?.type === 'logout') {
      useAuthStore.getState().logout();
    }
  };
}

/** Broadcast logout event to all other tabs */
export function broadcastLogout() {
  if (authChannel) {
    authChannel.postMessage({ type: 'logout' });
  }
  // Always set localStorage as fallback for tabs that don't support BroadcastChannel
  if (typeof window !== 'undefined') {
    localStorage.setItem('logout', Date.now().toString());
  }
}

// localStorage fallback: listen for logout from tabs without BroadcastChannel
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'logout') {
      useAuthStore.getState().logout();
    }
  });
}
